import React, { useMemo, useState } from 'react';
import { Alert, Button, Drawer, Empty, Progress, Select, Steps, Table, Upload, Spin } from 'antd';
import { UploadCloud, FileText, Download } from 'react-feather';
import { read, utils, SSF } from 'xlsx';
import axios from 'axios';
import moment from 'moment';
import '../../reports/front/guestCrm.css';
import { getContactSourceLabel, resolveContactSourceCode } from '../../utils/guestContactSources';

const apiUrl = process.env.REACT_APP_API_URL || 'https://api.example.com';
const MAX_IMPORT_ROWS = 2000;
const MAX_IMPORT_FILE_MB = 10;
const IMPORT_BATCH_SIZE = 50;
const CRM_DRAWER_Z_INDEX = 30000;
const CRM_SELECT_Z_INDEX = CRM_DRAWER_Z_INDEX + 10;
const FIELDS = [
  ['Full Name', ['name', 'guestname', 'fullname'], true],
  ['Contact Source', ['platform', 'bookingsource', 'guestsource', 'acquisitionsource', 'leadsource', 'originplatform']],
  ['Email', ['emailaddress', 'emailid']],
  ['Phone Number', ['phone', 'mobile', 'mobilenumber', 'contactnumber', 'phoneno']],
  ['Nationality', ['citizenship']], ['City', []], ['State', []], ['Country', []],
  ['Gender', []], ['Guest Title', ['salutation', 'title']],
  ['Date of Birth', ['dob', 'birthdate']], ['Marriage Anniversary', ['anniversary', 'anniversarydate']],
  ['Address', ['streetaddress']], ['Zip Code', ['pincode', 'postalcode', 'zip']],
  ['Is Regular', ['regular']], ['GST Number', ['gstin']], ['GST Company Name', []],
  ['GST Company Address', []], ['ID Proof Type', []], ['ID Proof Number', []],
  ['ID Proof File', []], ['ID Proof Issuing Authority', []], ['Passport Number', []],
  ['Passport Expiry', []], ['Visa Number', []], ['Visa Expiry', []], ['Additional Guests', []],
].map(([label, aliases = [], required = false]) => ({ label, aliases, required }));
const DATE_FIELDS = new Set(['Date of Birth', 'Marriage Anniversary', 'Passport Expiry', 'Visa Expiry']);
const normalize = text => String(text == null ? '' : text).toLowerCase().replace(/[^a-z0-9]/g, '');
const toExcelDate = (serial) => {
  const parsed = SSF.parse_date_code(serial);
  if (!parsed || parsed.y < 1900 || parsed.y > 2100) return null;
  const part = (v) => String(v).padStart(2, '0');
  return `${parsed.y}-${part(parsed.m)}-${part(parsed.d)}`;
};
const normalizeDate = (value) => {
  if (value === '' || value == null) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) return moment(value).format('YYYY-MM-DD');
  if (typeof value === 'number') return toExcelDate(value);
  const str = String(value).trim();
  if (moment(str, 'YYYY-MM-DD', true).isValid()) return str;
  // Do not guess between DD/MM and MM/DD when both are plausible.
  const slash = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (slash) {
    const a = Number(slash[1]); const b = Number(slash[2]);
    if (a <= 12 && b <= 12) return null;
    const pattern = a > 12 ? 'D/M/YYYY' : 'M/D/YYYY';
    const parsed = moment(str, pattern, true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }
  const parsed = moment(str, ['DD MMM YYYY', 'D MMM YYYY', 'MMM D, YYYY'], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};
const cleanValue = value => typeof value === 'string' ? value.trim() : value;
const describeValue = value => value === '' || value == null ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value);
const toRow = (row, mapping) => {
  const result = {};
  FIELDS.forEach(f => {
    if (mapping[f.label] === undefined) return;
    const val = cleanValue(row[mapping[f.label]]);
    if (val === '' || val == null) return;
    result[f.label] = f.label === 'Contact Source' ? (resolveContactSourceCode(val) || `INVALID:${String(val).trim()}`)
      : DATE_FIELDS.has(f.label) ? normalizeDate(val) : val;
  });
  return result;
};
const validateRow = (row, mode) => {
  if (!String(row['Full Name'] || '').trim()) return 'Full Name is missing';
  // New contact-only records can have no phone/email, but matching existing records cannot.
  if (mode !== 'Add New Records' && !String(row.Email || '').trim() && !String(row['Phone Number'] || '').trim())
    return 'Phone or email is required to identify an existing guest in this import mode';
  if (row['Contact Source'] && !resolveContactSourceCode(row['Contact Source']))
    return 'Contact Source must be one of the nine built-in sources (Airbnb, MMT, etc.)';
  for (const key of DATE_FIELDS) if (row[key] === null) return `${key} has an ambiguous or invalid date; use YYYY-MM-DD`;
  if (row['Is Regular'] !== undefined && !['true', 'false', 'yes', 'no', '1', '0'].includes(String(row['Is Regular']).toLowerCase())) return 'Is Regular must be yes/no';
  return '';
};

const GuestImportModal = ({ visible, onClose, onImportSuccess }) => {
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState('Add New Records');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [sourceRowNumbers, setSourceRowNumbers] = useState([]);
  const [progress, setProgress] = useState(0);
  const [mapping, setMapping] = useState({});
  const [readError, setReadError] = useState('');
  const [serverError, setServerError] = useState('');
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const reset = () => { setStep(0); setFileName(''); setHeaders([]); setRawRows([]); setSourceRowNumbers([]); setProgress(0); setMapping({}); setReadError(''); setServerError(''); setSummary(null); setBusy(false); setFileLoading(false); setMode('Add New Records'); };
  const close = () => { if (busy || fileLoading) return; reset(); onClose(); };
  const columnChoices = headers.map((h, i) => ({ value: i, label: `${String(h || `Column ${i + 1}`)} · ${i + 1}` }));
  const autoMap = (names) => {
    const used = new Set(); const newMapping = {};
    FIELDS.forEach(field => {
      const possibilities = [field.label, ...field.aliases].map(normalize);
      const position = names.findIndex((name, i) => !used.has(i) && possibilities.includes(normalize(name)));
      if (position >= 0) { newMapping[field.label] = position; used.add(position); }
    });
    return newMapping;
  };
  const loadFile = (file) => {
    setReadError('');
    if (!/\.(csv|xlsx|xls)$/i.test(file.name)) { setReadError('Use an Excel (.xlsx/.xls) or CSV (.csv) file.'); return false; }
    if (file.size > MAX_IMPORT_FILE_MB * 1024 * 1024) { setReadError(`Maximum supported file size is ${MAX_IMPORT_FILE_MB} MB.`); return false; }
    setFileLoading(true);
    const reader = new FileReader();
    reader.onerror = () => { setReadError('Could not read this file.'); setFileLoading(false); };
    reader.onload = (event) => {
      try {
        const bytes = new Uint8Array(event.target.result);
        const workbook = read(bytes, { type: 'array', cellDates: true });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!sheet) throw new Error('The workbook has no usable worksheet.');
        const grid = utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true, blankrows: false });
        if (grid.length < 2) throw new Error('Add a header row and at least one guest row.');
        const sourceHeaders = grid[0].map(h => String(h || '').trim());
        if (!sourceHeaders.some(Boolean)) throw new Error('The first row must contain column headings.');
        const nonEmptyRows = grid.slice(1).map((cells, i) => ({ cells, line: i + 2 }))
          .filter(item => item.cells.some(cell => cell !== '' && cell != null));
        const rows = nonEmptyRows.map(item => item.cells);
        if (!rows.length) throw new Error('No guest records were found.');
        if (rows.length > MAX_IMPORT_ROWS) throw new Error(`Import up to ${MAX_IMPORT_ROWS.toLocaleString()} guests at a time. Split larger files into batches.`);
        setHeaders(sourceHeaders); setRawRows(rows); setSourceRowNumbers(nonEmptyRows.map(item => item.line)); setMapping(autoMap(sourceHeaders));
        setFileName(file.name); setStep(1);
      } catch (err) { setReadError(err.message || 'Unable to parse spreadsheet.'); }
      finally { setFileLoading(false); }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };
  const mappedRows = useMemo(() => rawRows.map((r, i) => ({ ...toRow(r, mapping), _rowNumber: sourceRowNumbers[i] || i + 2 })), [rawRows, mapping, sourceRowNumbers]);
  const rowErrors = useMemo(() => mappedRows.map(row => ({ row: row._rowNumber, name: row['Full Name'] || 'Unknown', reason: validateRow(row, mode) })).filter(r => !!r.reason), [mappedRows, mode]);
  const mappingErrors = useMemo(() => {
    const choices = Object.values(mapping);
    if (new Set(choices).size !== choices.length) return 'A spreadsheet column cannot be mapped to multiple guest fields.';
    if (mapping['Full Name'] === undefined) return 'Map the Full Name column.';
    if (mode !== 'Add New Records' && mapping.Email === undefined && mapping['Phone Number'] === undefined)
      return 'Map a Phone Number or Email column to identify existing guests.';
    return '';
  }, [mapping, mode]);
  const downloadTemplate = () => {
    const csv = 'Full Name,Phone Number,Email,Contact Source,Nationality,City,Country\r\n';
    const file = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(file);
    const a = document.createElement('a'); a.href = url; a.download = 'netshine_guest_import_template.csv'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };
  const submit = async () => {
    if (rowErrors.length || mappingErrors || busy) return;
    setBusy(true); setServerError(''); setProgress(0);
    const complete = { total: 0, successful: [], failed: [], added: 0, updated: 0 };
    try {
      // Sequential 50-row requests preserve the existing backend per-request cap and avoid large JSON bodies.
      // Each request independently validates and locks the authenticated company.
      for (let offset = 0; offset < mappedRows.length; offset += IMPORT_BATCH_SIZE) {
        const batch = mappedRows.slice(offset, offset + IMPORT_BATCH_SIZE);
        const cleanRows = batch.map(({ _rowNumber, ...row }) => row);
        const rowNumbers = batch.map(row => row._rowNumber);
        const response = await axios.post(`${apiUrl}/reports-daily/import-guest/`,
          { data: cleanRows, rowNumbers, importMode: mode },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
        if (!response.data || response.data.ok !== true || !Array.isArray(response.data.success)) {
          throw new Error('Unexpected response from import API.');
        }
        complete.total += Number(response.data.total) || batch.length;
        complete.successful.push(...response.data.success);
        complete.failed.push(...(response.data.failed || []));
        complete.added += Number(response.data.added) || 0;
        complete.updated += Number(response.data.updated) || 0;
        setProgress(Math.min(offset + batch.length, mappedRows.length));
      }
      setSummary(complete); setStep(3);
    } catch (error) {
      setServerError(`${error.response?.data?.message || error.message || 'Import could not be completed.'} ${complete.total > 0 ? `Only the first ${complete.total} rows were processed; remaining rows were not submitted.` : ''}`);
      setSummary(complete); setStep(3);
    } finally {
      if (complete.successful.length && typeof onImportSuccess === 'function') onImportSuccess();
      setBusy(false);
    }
  };
  const previewCols = [
    { title: 'EXCEL ROW', dataIndex: '_rowNumber', key: 'row', width: 100 },
    { title: 'NAME', dataIndex: 'Full Name', key: 'name' },
    { title: 'EMAIL', dataIndex: 'Email', key: 'email', render: describeValue },
    { title: 'PHONE', dataIndex: 'Phone Number', key: 'phone', render: describeValue },
    { title: 'CONTACT SOURCE', dataIndex: 'Contact Source', key: 'source',
      render: value => value && resolveContactSourceCode(value) ? getContactSourceLabel(value) : describeValue(value) },
    { title: 'VALIDATION', key: 'status', render: (_, row) => {
      const error = validateRow(row, mode);
      return error ? <span style={{ color: '#d34e51' }}>{error}</span> : <span style={{ color: '#22906d' }}>Ready</span>;
    } },
  ];
  return <Drawer className="gcrm-import-drawer" title={<span className="gcrm-drawer-title">Import guests</span>}
    placement="right" width={770} zIndex={CRM_DRAWER_Z_INDEX} visible={visible} onClose={close}
    closable={!busy && !fileLoading} maskClosable={!busy && !fileLoading} keyboard={!busy && !fileLoading} destroyOnClose>
    <p className="gcrm-import-headline">Bring existing guest records into Netshine Hospitality with a controlled, reviewed import.</p>
    <Steps current={step} size="small">
      <Steps.Step title="Upload" /><Steps.Step title="Map fields" /><Steps.Step title="Validate" /><Steps.Step title="Result" />
    </Steps>
    <div className="gcrm-import-card">
      {step === 0 && <>
        <h3 className="gcrm-import-step-heading">Upload your guest spreadsheet</h3>
        <p className="gcrm-import-step-note">Excel or CSV, up to {MAX_IMPORT_ROWS.toLocaleString()} rows and {MAX_IMPORT_FILE_MB} MB. Only the first worksheet is imported.</p>
        <Spin spinning={fileLoading}><Upload.Dragger className="gcrm-upload-zone" multiple={false} accept=".xlsx,.xls,.csv"
          beforeUpload={loadFile} showUploadList={false} disabled={fileLoading}>
          <p><UploadCloud size={30} color="#4165c6" /></p><p style={{ fontWeight: 700, color: '#334a66' }}>Choose a file or drag it here</p>
          <p className="gcrm-upload-formats">XLSX, XLS, CSV</p>
        </Upload.Dragger></Spin>
        {readError && <Alert style={{ marginTop: 15 }} type="error" showIcon message={readError} />}
        <Button type="link" icon={<Download size={14} />} onClick={downloadTemplate} style={{ paddingLeft: 0, marginTop: 14 }}>Download blank CSV template</Button>
        <div className="gcrm-import-note">Guest records are always stored under the company in your authenticated session. Existing identity-verification status cannot be imported or overwritten.</div>
      </>}
      {step === 1 && <>
        <h3 className="gcrm-import-step-heading">Match the spreadsheet columns</h3>
        <p className="gcrm-import-step-note"><FileText size={14} style={{ marginRight: 6 }} />{fileName} · {rawRows.length} guest rows<br />Review the automatic mapping. Full Name is required; phone and email are optional for new guest records. The Platform column maps to Contact Source, not to a reservation.</p>
        <div style={{ marginBottom: 13 }}><label style={{ display: 'block', marginBottom: 5, fontSize: 11, color: '#7b899b' }}>IMPORT MODE</label>
          <Select value={mode} style={{ width: '100%' }} dropdownStyle={{ zIndex: CRM_SELECT_Z_INDEX }} onChange={setMode}>
            <Select.Option value="Add New Records">Add new only — skip existing contacts</Select.Option>
            <Select.Option value="Add/Update Records">Add or update — update matched profiles</Select.Option>
            <Select.Option value="Update Existing Records">Update only — do not create new profiles</Select.Option>
          </Select></div>
        <div className="gcrm-import-note">Contact Source uses Netshine's fixed channel list only. Excel values Airbnb and MakeMyTrip map to Airbnb (-101) and MMT (-102). No booking is created.</div>
        <div className="gcrm-import-fields">
          {FIELDS.map(f => <div className="gcrm-import-field" key={f.label}>
            <label>{f.label}{f.required && <span style={{ color: '#d65e58' }}> *</span>}</label>
            <Select allowClear value={mapping[f.label]} placeholder="Not mapped" dropdownStyle={{ zIndex: CRM_SELECT_Z_INDEX }}
              onChange={idx => setMapping(old => { const update = { ...old }; if (idx === undefined) delete update[f.label]; else update[f.label] = idx; return update; })}
              options={columnChoices} showSearch optionFilterProp="label" />
          </div>)}
        </div>
        {mappingErrors && <Alert type="warning" showIcon message={mappingErrors} style={{ marginTop: 14 }} />}
        <div className="gcrm-import-step-actions">
          <Button onClick={() => { reset(); }}>Change file</Button>
          <Button onClick={() => setMapping(autoMap(headers))}>Auto-map again</Button>
          <Button type="primary" disabled={!!mappingErrors} onClick={() => setStep(2)}>Review records</Button>
        </div>
      </>}
      {step === 2 && <>
        <h3 className="gcrm-import-step-heading">Validate before import</h3>
        <p className="gcrm-import-step-note">Preview the first 10 rows. All {mappedRows.length} rows are validated locally before submission; the server validates them again before saving.</p>
        <div className="gcrm-import-summary"><div><strong>{mappedRows.length}</strong><small>Guest rows</small></div>
          <div><strong>{mappedRows.length - rowErrors.length}</strong><small>Ready</small></div><div><strong>{rowErrors.length}</strong><small>Needs correction</small></div></div>
        <Table size="small" scroll={{ x: 760 }} rowKey="_rowNumber" dataSource={mappedRows.slice(0, 10)} columns={previewCols} pagination={false} />
        {mode === 'Add New Records' && mappedRows.some(row => !row.Email && !row['Phone Number']) &&
          <Alert style={{ marginTop: 15 }} type="warning" showIcon
            message={`${mappedRows.filter(row => !row.Email && !row['Phone Number']).length} guest profile(s) have no phone or email`}
            description="These rows can be created, but cannot be reliably matched to existing guests in future imports. Review the names before proceeding." />}
        {rowErrors.length > 0 && <Alert style={{ marginTop: 15 }} type="error" showIcon
          message={`${rowErrors.length} rows need correction`} description={rowErrors.slice(0, 6).map(r => `Row ${r.row}: ${r.reason}`).join(' • ')} />}
        <div className="gcrm-import-note">Existing guests are matched within your company by email or phone. New guests may have neither contact field. Ambiguous matches are rejected; blank spreadsheet cells will not erase existing information.</div>
        {busy && <div className="gcrm-import-progress" role="status" aria-live="polite">
          <div className="gcrm-import-progress-heading"><strong>Import in progress</strong><span>{progress.toLocaleString()} / {mappedRows.length.toLocaleString()} rows processed</span></div>
          <Progress percent={Math.round((progress / mappedRows.length) * 100)} size="small" status="active" strokeColor="#2758c8" />
          <p>Records are imported in batches of {IMPORT_BATCH_SIZE}. Please keep this window open until the result appears.</p>
        </div>}
        <div className="gcrm-import-step-actions">
          <Button disabled={busy} onClick={() => setStep(1)}>Back to mapping</Button><Button type="primary" loading={busy}
            disabled={rowErrors.length > 0 || !!mappingErrors} onClick={submit}>Import {mappedRows.length} guests</Button>
        </div>
      </>}
      {step === 3 && <>
        <h3 className="gcrm-import-step-heading">Import result</h3>
        {progress > 0 && <p className="gcrm-import-step-note">{progress} of {mappedRows.length} guest rows submitted.</p>}
        {busy ? <Spin /> : summary ? <>
          {serverError && <Alert type="error" showIcon message="Import stopped before completion" description={serverError} style={{ marginBottom: 14 }} />}
          <div className="gcrm-import-summary"><div><strong>{summary.total || 0}</strong><small>Processed</small></div>
            <div><strong>{(summary.successful || []).length}</strong><small>Added / updated</small></div>
            <div><strong>{(summary.failed || []).length}</strong><small>Not imported</small></div></div>
          {(summary.successful || []).length > 0 ? <Alert type="success" showIcon message="Successfully imported records were refreshed in Guest CRM." /> : <Alert type="warning" showIcon message="No guests were imported. Review the issues below." />}
          {(summary.failed || []).length > 0 && <Table style={{ marginTop: 16 }} rowKey={(r, i) => `${r.row}-${i}`} size="small" dataSource={summary.failed}
            columns={[{ title: 'ROW', dataIndex: 'row' }, { title: 'GUEST', dataIndex: 'name' }, { title: 'REASON', dataIndex: 'reason' }]}
            pagination={{ pageSize: 5 }} />}
        </> : <Empty description="No import results are available." />}
        <div className="gcrm-import-step-actions"><Button onClick={close}>Done</Button>
          {serverError && (!summary || summary.total === 0) && <Button type="primary" onClick={() => { setServerError(''); setStep(2); }}>Try again</Button>}</div>
      </>}
    </div>
  </Drawer>;
};
export default GuestImportModal;
